'use client';

import { useRouter } from 'next/navigation';
import { createContext, useContext, useEffect, useState } from 'react';
import type { Locale } from '@/lib/i18n/config';
import { getHtmlLang, i18n, localeCookieName } from '@/lib/i18n/config';
import { useTranslationContext } from './translation-provider';

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

// The server reads this cookie to localize metadata such as the tab title.
export function writeLocaleCookie(locale: Locale) {
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API not widely supported
  document.cookie = `${localeCookieName}=${locale}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; samesite=lax${secure}`;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(i18n.defaultLocale);
  const { preloadDictionary } = useTranslationContext();
  const router = useRouter();

  useEffect(() => {
    const savedLocale = localStorage.getItem('locale') as Locale;
    if (savedLocale && i18n.locales.includes(savedLocale)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reading from localStorage (external system) on mount
      setLocaleState(savedLocale);
    }
  }, []);

  // Preload translations when locale changes
  useEffect(() => {
    preloadDictionary(locale);
    // Also preload default locale as fallback
    preloadDictionary(i18n.defaultLocale);
  }, [locale, preloadDictionary]);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('locale', newLocale);
    writeLocaleCookie(newLocale);
    // Re-render server metadata so the tab title switches language too.
    router.refresh();
    // Update document lang attribute
    document.documentElement.lang = getHtmlLang(newLocale);
    // Preload the new locale's translations
    preloadDictionary(newLocale);
  };

  useEffect(() => {
    // Set initial document lang
    document.documentElement.lang = getHtmlLang(locale);
    writeLocaleCookie(locale);
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
}
